import {
  MarkedCellType,
  type CellState,
  type MarkedCell,
  type Tutorial,
  type TutorialStep,
  type TutorialTasks,
} from '@puzzlio/core';
import { meta } from './config';
import { CLUE_GUTTER, CROSSED_CELL, FILLED_CELL, cellValue, type MosaicBoard } from './types';

type Coord = [row: number, col: number];

const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
/** The cells of row `r` at the given columns. */
const inRow = (r: number, cols: number[]): Coord[] => cols.map((c) => [r, c]);
/** The cells of column `c` at the given rows. */
const inCol = (c: number, rows: number[]): Coord[] => rows.map((r) => [r, c]);

const valuesAre = (cells: CellState[][], coords: Coord[], want: string) =>
  coords.every(([r, c]) => cellValue(cells[r]?.[c] ?? 0) === want);
const allFilled = (cells: CellState[][], coords: Coord[]) => valuesAre(cells, coords, FILLED_CELL);
const allCrossed = (cells: CellState[][], coords: Coord[]) => valuesAre(cells, coords, CROSSED_CELL);

const mark = (row: number, col: number, markType: MarkedCellType): MarkedCell => ({ row, col, markType });
const highlightRowClue = (r: number) => mark(r, CLUE_GUTTER, MarkedCellType.highlight);
const highlightColClue = (c: number) => mark(CLUE_GUTTER, c, MarkedCellType.highlight);

/* Show the player exactly which mark each target cell wants. The grid drops a hint as soon as the
   cell holds what it asks for, so these double as a progress readout for the step. */
const wantFilled = (coords: Coord[]) => coords.map(([r, c]) => mark(r, c, MarkedCellType.hintFill));
const wantCrossed = (coords: Coord[]) => coords.map(([r, c]) => mark(r, c, MarkedCellType.hintCross));

/**
 * Grey out (and lock) every cell except `enabled`.
 *
 * Pass exactly the cells the step's own answer covers — never the whole line it is talking about.
 * Anything left enabled beyond the answer is a cell the player can get wrong: at this point in the
 * solve its true value is still unknown, so a wrong mark there is undetectable and sticks around to
 * poison a later step.
 */
const enableOnly = (board: MosaicBoard, enabled: Coord[], ...extraMarks: MarkedCell[]): MarkedCell[] => {
  const keep = new Set(enabled.map(([r, c]) => `${r},${c}`));
  const marks: MarkedCell[] = [...extraMarks];
  for (let r = 0; r < board.height; r++) {
    for (let c = 0; c < board.width; c++) {
      if (!keep.has(`${r},${c}`)) marks.push(mark(r, c, MarkedCellType.disabled));
    }
  }
  return marks;
};

const tasks = (...items: [text: string, done: boolean][]): TutorialTasks => ({
  kind: 'tasks',
  tasks: items.map(([text, done]) => ({ text, done })),
});

/** A step with work to do on the board, which locks once it's done. */
const lesson = (blocks: (string | TutorialTasks)[], done: boolean, marks: MarkedCell[]): TutorialStep => ({
  message: { kind: 'instruction', blocks, done },
  marks,
  canInteract: !done,
});

/*
  These are written against the `tutorial-1` board: a rabbit 5 wide and 10 tall whose clues are
    rows  [0] [1 1] [1 1] [3] [3] [1 1 1] [5] [5] [3] [0]
    cols  [3] [4 3] [6] [4 3] [3]
  Each step teaches one deduction and unlocks only the lines that deduction applies to, so the
  coordinates below are deliberately hard-coded to that board. Every step's cells are forced by the
  clues plus what the earlier steps established — nothing here asks the player to guess — and the
  cells left over after the last taught step fall to the same line logic.
*/

/** The clue the intro step reads aloud: "4 3", short enough to spell out in a sentence. */
const EXAMPLE_CLUE_COL = 1;
/** Clue 5 in a 5-wide row: the run is the whole line. */
const FULL_ROWS = [6, 7];
/** Clue 6 in a 10-tall column, so the run covers rows 4-5 wherever it sits. */
const OVERLAP_COLS = [2];
const OVERLAP_ROWS = range(4, 5);
/** Clue 0: the line is empty end to end. */
const EMPTY_ROWS = [0, 9];
/**
 * Clue "1 1 1": three filled cells plus the two gaps they need is exactly the 5-cell width. The
 * middle cell is already filled by the overlap step, so only the two ends are left to place.
 */
const EXACT_ROWS = [5];
const EXACT_FILLS = [0, 4];
const EXACT_GAPS = [1, 3];
/** Clue 3, and rows 5-7 hold all three of them once the rows above are done. */
const SATISFIED_COLS = [0, 4];
const SATISFIED_GAPS = [1, 2, 3, 4, 8];

export const tutorial: Tutorial<MosaicBoard, CellState> = {
  game: 'mosaic',
  phases: [{ slug: meta.tutorialSlug, firstStep: 0 }],
  steps: [
    // 0 — Welcome. Read-only: nothing to do but read.
    () => ({
      message: {
        kind: 'instruction',
        heading: 'Welcome to Mosaic!',
        blocks: [
          'Shade the correct cells and a hidden picture appears.',
          'The numbers around the board tell you how to solve it. Numbers above each column show how many cells to shade down. Numbers next to each row show how many to shade across.',
        ],
        done: true,
        showReset: false,
      },
      canInteract: false,
    }),

    // 1 — Reading a clue, with the one being explained highlighted. Read-only like the welcome.
    () => ({
      message: {
        kind: 'instruction',
        blocks: [
          "Sometimes you'll see multiple numbers.",
          "'4 3' means a block of 4 shaded cells, then a gap (at least one empty cell), then a block of 3 shaded cells.",
          'The blocks must be separated.',
        ],
        done: true,
      },
      marks: [highlightColClue(EXAMPLE_CLUE_COL)],
      canInteract: false,
    }),

    // 2 — A clue as long as the line fills the whole line.
    ({ board, cells }) => {
      const targets = FULL_ROWS.flatMap((r) => inRow(r, range(0, board.width - 1)));
      const done = allFilled(cells, targets);
      return lesson(
        ["The clues for these rows is 5 and they're 5 wide. Shade every cell.", tasks(['Fill both rows.', done])],
        done,
        enableOnly(board, targets, ...FULL_ROWS.map(highlightRowClue), ...wantFilled(targets))
      );
    },

    // 3 — Overlap: a 6-run in a 10-tall column pins down its middle two cells.
    ({ board, cells }) => {
      // Only the overlap itself is unlocked: the cells above and below it are still undecided at this
      // point, so leaving them open would let the player fill a cell the step can't mark wrong.
      const targets = OVERLAP_COLS.flatMap((c) => inCol(c, OVERLAP_ROWS));
      const done = allFilled(cells, targets);
      return lesson(
        [
          'No matter where the 6-block goes in this column, it always reaches these middle cells.',
          tasks(['Shade the middle cells.', done]),
        ],
        done,
        enableOnly(board, targets, ...OVERLAP_COLS.map(highlightColClue), ...wantFilled(targets))
      );
    },

    // 4 — A clue of 0. The cheapest line on the board, and where the ✕ gets introduced.
    ({ board, cells }) => {
      const targets = EMPTY_ROWS.flatMap((r) => inRow(r, range(0, board.width - 1)));
      const done = allCrossed(cells, targets);
      return lesson(
        [
          'A clue of 0 means the row is empty. Fill the row with ✕s to help you remember that.',
          'Tap twice for an ✕.',
          tasks(['✕ off both rows.', done]),
        ],
        done,
        enableOnly(board, targets, ...EMPTY_ROWS.map(highlightRowClue), ...wantCrossed(targets))
      );
    },

    // 5 — A line the clue fills exactly, so the gaps between its runs are forced too.
    ({ board, cells }) => {
      const fills = EXACT_ROWS.flatMap((r) => inRow(r, EXACT_FILLS));
      const crosses = EXACT_ROWS.flatMap((r) => inRow(r, EXACT_GAPS));
      const filled = allFilled(cells, fills);
      const crossed = allCrossed(cells, crosses);
      return lesson(
        [
          "The clue '1 1 1' needs gaps between blocks. That fills this row exactly.",
          tasks(['Fill the two ends.', filled], ['✕ the gaps.', crossed]),
        ],
        filled && crossed,
        enableOnly(
          board,
          [...fills, ...crosses],
          ...EXACT_ROWS.map(highlightRowClue),
          ...wantFilled(fills),
          ...wantCrossed(crosses)
        )
      );
    },

    // 6 — A satisfied line: everything else in it is known-empty.
    ({ board, cells }) => {
      // The three filled cells stay locked — tapping one would cycle it to an ✕.
      const targets = SATISFIED_COLS.flatMap((c) => inCol(c, SATISFIED_GAPS));
      const done = allCrossed(cells, targets);
      return lesson(
        [
          "You've found the 3-block in both columns. Everything else is empty.",
          tasks(['✕ the rest of the columns.', done]),
        ],
        done,
        enableOnly(board, targets, ...SATISFIED_COLS.map(highlightColClue), ...wantCrossed(targets))
      );
    },

    // 7 — Off the rails: the rest of the board with no marks and no restrictions.
    ({ complete }) => ({
      message: {
        kind: 'instruction',
        blocks: [
          "That's every trick you need: overlaps, lines that only fit one way, and using X's to narrow things down. You've got this.",
          tasks(['Finish the puzzle to reveal the picture.', complete]),
        ],
        done: complete,
        nextLabel: complete ? 'Ta-da!' : undefined,
      },
    }),

    // 8 — Done.
    () => ({
      message: {
        kind: 'complete',
        title: 'Tutorial Complete! 🎉',
        description: 'You revealed the picture! Now you know everything you need to solve a daily puzzle.',
      },
    }),
  ],
};
